import type { CropState } from './EditPhotoContent';
import { zoomToScale } from './EditPhotoContent';

// Virtual container is always 4:3 (1200×900) — crop box coords are in this space.
const W = 1200;
const H = 900;

export type OutputRatio = '4:3' | '5:4' | '16:9' | '3:2' | '1:1';

// Output canvas heights for each ratio at W=1200.
const OUTPUT_HEIGHTS: Record<OutputRatio, number> = {
  '4:3': 900,
  '5:4': 960,
  '16:9': 675,
  '3:2': 800,
  '1:1': 1200,
};

export async function cropImageToBlob(
  imageUrl: string,
  cropState: CropState,
  outputRatio: OutputRatio = '4:3',
): Promise<File> {
  const { zoom, rotate, cropBox } = cropState;
  const scale = zoomToScale(zoom);
  const rad = (rotate * Math.PI) / 180;

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Failed to load image for crop'));
    el.src = imageUrl;
  });

  // objectFit: cover — scale the image so it fills the virtual container
  const coverScale = Math.max(W / img.naturalWidth, H / img.naturalHeight);
  const imgW = img.naturalWidth * coverScale;
  const imgH = img.naturalHeight * coverScale;

  // Crop box in virtual container pixels.
  // cropBox coords are always relative to the 4:3 space (W×H).
  const cropX = (cropBox.left / 100) * W;
  const cropW = (cropBox.size / 100) * W;
  const cropH43 = (cropBox.size / 100) * H;

  // For non-4:3 output: keep the same horizontal crop and center vertically.
  const cropH = (cropW / W) * OUTPUT_HEIGHTS[outputRatio];
  const cropY = (cropBox.top / 100) * H + cropH43 / 2 - cropH / 2;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(cropW);
  canvas.height = Math.round(cropH);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  // Replicate CSS: transform: scale(scale) rotate(rotate); transform-origin: center
  // applied to the cover-fitted image centered in the virtual container.
  ctx.save();
  ctx.translate(-cropX, -cropY);
  ctx.translate(W / 2, H / 2);
  ctx.scale(scale, scale);
  ctx.rotate(rad);
  ctx.translate(-W / 2, -H / 2);
  ctx.drawImage(img, (W - imgW) / 2, (H - imgH) / 2, imgW, imgH);
  ctx.restore();

  let blob: Blob;
  try {
    blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        b => (b ? resolve(b) : reject(new Error('Canvas export failed'))),
        'image/jpeg',
        0.9,
      );
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'SecurityError') {
      throw new Error('Unable to crop this image. Please use Replace to upload a new version.');
    }
    throw err;
  }

  return new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
}
